// src/components/course/manager/CreateLesson.jsx
import React, { useState, useEffect } from 'react';
import {
  Save, ArrowLeft, Upload, Play, FileText, HelpCircle, Image as ImageIcon,
  Clock, Eye, Plus, Edit3, X, CheckCircle, Target, Brain, MessageSquare,
  Star, GripVertical, Trash2
} from 'lucide-react';
import { courseService } from '../../../services/courseService';
import { firestoreService } from '../../../services/firestoreService';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const CreateLesson = ({ bookId, courseId, lessonId, user, onNavigate }) => {
  const isEditing = !!lessonId;
  
  const [course, setCourse] = useState(null);
  const [lesson, setLesson] = useState(null);
  const [contentBlocks, setContentBlocks] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [loading, setLoading] = useState(true);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    duration: 5,
    isPreview: false
  });

  const [errors, setErrors] = useState({});

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    loadData();
  }, [courseId, lessonId, bookId]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load course
      const courseData = await courseService.getCourse(bookId, courseId);
      setCourse(courseData);

      // Load lesson if editing
      if (isEditing) {
        const lessonData = await courseService.getLesson(bookId, courseId, lessonId);
        if (lessonData) {
          setLesson(lessonData);
          setFormData({
            title: lessonData.title || '',
            description: lessonData.description || '',
            duration: lessonData.duration || 5,
            isPreview: lessonData.isPreview || false
          });
          setContentBlocks(lessonData.contentBlocks || []);
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
      alert('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (contentBlocks.length === 0) {
      newErrors.blocks = 'Please add at least one content block';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      if (errors.blocks) {
        alert(errors.blocks);
      }
      return;
    }

    if (!courseId) {
      alert('Course ID is missing');
      return;
    }

    try {
      setSaving(true);

      const lessonData = {
        ...formData,
        // REMOVE courseId from here - it's passed as parameter
        creatorId: user.uid,
        duration: parseInt(formData.duration),
        contentBlocks: contentBlocks.map((block, index) => ({
          ...block,
          order: index
        })),
        order: isEditing ? lesson.order : await getNextLessonOrder(),
        updatedAt: new Date()
      };

      if (isEditing) {
        await courseService.updateLesson(bookId, courseId, lessonId, lessonData);
        alert('Lesson updated successfully!');
      } else {
        const newLessonId = await courseService.createLesson(bookId, courseId, lessonData);
        alert('Lesson created successfully!');
        onNavigate('creator-detail', { courseId });
        return;
      }

      onNavigate('creator-detail', { courseId });

    } catch (error) {
      console.error('Error saving lesson:', error);
      alert('Failed to save lesson');
    } finally {
      setSaving(false);
    }
  };

  const getNextLessonOrder = async () => {
    try {
      const lessons = await courseService.getLessons(bookId, courseId);
      return lessons.length;
    } catch (error) {
      return 0;
    }
  };

  // Content Block Management
  const addContentBlock = (type) => {
    const newBlock = {
      id: `block_${Date.now()}`,
      type,
      title: '',
      content: '',
      instructions: '',
      order: contentBlocks.length
    };

    switch (type) {
      case 'video':
        newBlock.title = 'Video Content';
        newBlock.videoUrl = '';
        newBlock.videoFileUrl = '';
        break;
      case 'text':
        newBlock.title = 'Text Content';
        newBlock.content = '';
        break;
      case 'quiz':
        newBlock.title = 'Knowledge Check';
        newBlock.quizQuestions = [{
          question: '',
          options: ['', '', '', ''],
          correctAnswer: 0,
          explanation: ''
        }];
        break;
      case 'assessment':
        newBlock.title = 'Interactive Assessment';
        newBlock.questions = [{
          type: 'multiple-choice',
          question: '',
          options: ['', '', '', ''],
          correctAnswer: 0,
          explanation: ''
        }];
        break;
      case 'image':
        newBlock.title = 'Image/Infographic';
        newBlock.imageUrl = '';
        newBlock.imageFileUrl = '';
        break;
    }

    setContentBlocks([...contentBlocks, newBlock]);
  };

  const updateContentBlock = (blockId, updates) => {
    setContentBlocks(blocks =>
      blocks.map(block =>
        block.id === blockId ? { ...block, ...updates } : block
      )
    );
  };

  const removeContentBlock = (blockId) => {
    setContentBlocks(blocks => blocks.filter(block => block.id !== blockId));
  };

  const duplicateContentBlock = (blockId) => {
    const blockToDuplicate = contentBlocks.find(block => block.id === blockId);
    if (blockToDuplicate) {
      const duplicatedBlock = {
        ...blockToDuplicate,
        id: `block_${Date.now()}`,
        title: `${blockToDuplicate.title} (Copy)`
      };
      const blockIndex = contentBlocks.findIndex(block => block.id === blockId);
      const newBlocks = [...contentBlocks];
      newBlocks.splice(blockIndex + 1, 0, duplicatedBlock);
      setContentBlocks(newBlocks);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setContentBlocks((blocks) => {
        const oldIndex = blocks.findIndex(block => block.id === active.id);
        const newIndex = blocks.findIndex(block => block.id === over.id);
        return arrayMove(blocks, oldIndex, newIndex);
      });
    }
  };

  const handleFileUpload = async (file, type, blockId) => {
    try {
      setUploadProgress(10);
      const uploadPath = `pb-${bookId}/courses/${courseId}/lessons/${lessonId || 'new'}/${type}_${blockId}_${Date.now()}`;
      
      const downloadURL = await firestoreService.uploadFile(file, uploadPath, (progress) => {
        setUploadProgress(progress);
      });
      
      updateContentBlock(blockId, { [`${type}FileUrl`]: downloadURL });
      alert('File uploaded successfully!');
      setUploadProgress(0);
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Failed to upload file');
      setUploadProgress(0);
    }
  };

  const contentBlockTypes = [
    { type: 'video', label: 'Video', icon: Play, description: 'Video content from URL or upload' },
    { type: 'text', label: 'Text/Article', icon: FileText, description: 'Rich text content with markdown' },
    { type: 'image', label: 'Image', icon: ImageIcon, description: 'Images and infographics' },
    { type: 'quiz', label: 'Quick Quiz', icon: HelpCircle, description: 'Simple multiple choice questions' },
    { type: 'assessment', label: 'Interactive Assessment', icon: Target, description: 'Advanced assessments with drag-drop, matching, etc.' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => onNavigate('creator-detail', { courseId })}
            className="text-gray-400 hover:text-gray-600"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isEditing ? 'Edit Lesson' : 'Create New Lesson'}
            </h1>
            <p className="text-gray-600">for {course?.title}</p>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <button
            type="button"
            onClick={() => setShowPreview(!showPreview)}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Eye className="w-4 h-4" />
            <span>{showPreview ? 'Edit' : 'Preview'}</span>
          </button>

          <button
            onClick={handleSubmit}
            disabled={saving || contentBlocks.length === 0}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
          >
            <Save className="w-5 h-5" />
            <span>{saving ? (isEditing ? 'Updating...' : 'Creating...') : (isEditing ? 'Update Lesson' : 'Create Lesson')}</span>
          </button>
        </div>
      </div>

      {showPreview ? (
        <LessonPreview
          lesson={{
            ...formData,
            contentBlocks
          }}
          onClose={() => setShowPreview(false)}
        />
      ) : (
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Information */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Lesson Details</h3>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Lesson Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => {
                    setFormData({ ...formData, title: e.target.value });
                    if (errors.title) setErrors({ ...errors, title: null });
                  }}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter lesson title"
                />
                {errors.title && (
                  <p className="text-red-500 text-sm mt-1">{errors.title}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Brief description of the lesson content"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Duration (minutes)
                  </label>
                  <input
                    type="number"
                    value={formData.duration}
                    onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) || 0 })}
                    min="1"
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="5"
                  />
                </div>

                <div className="flex items-end">
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.isPreview}
                      onChange={(e) => setFormData({ ...formData, isPreview: e.target.checked })}
                      className="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Available as preview
                    </span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Content Blocks */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Lesson Content</h3>
              <span className="text-sm text-gray-500">
                {contentBlocks.length} block{contentBlocks.length !== 1 ? 's' : ''}
              </span>
            </div>

            {/* Add Content Block Buttons */}
            <div className="mb-6">
              <p className="text-sm text-gray-600 mb-4">Choose content types to add to your lesson:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {contentBlockTypes.map(({ type, label, icon: Icon, description }) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => addContentBlock(type)}
                    className="flex items-start space-x-3 p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors text-left"
                  >
                    <Icon className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-medium text-gray-900">{label}</h4>
                      <p className="text-xs text-gray-500 mt-1">{description}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Content Blocks List */}
            {contentBlocks.length > 0 ? (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={contentBlocks.map(block => block.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-4">
                    {contentBlocks.map((block, index) => (
                      <SortableContentBlock
                        key={block.id}
                        block={block}
                        index={index}
                        onUpdate={updateContentBlock}
                        onRemove={removeContentBlock}
                        onDuplicate={duplicateContentBlock}
                        onFileUpload={handleFileUpload}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <Plus className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h4 className="text-lg font-medium text-gray-900 mb-2">No content blocks yet</h4>
                <p className="text-gray-600">Add content blocks above to build your lesson</p>
              </div>
            )}
          </div>

          {/* Interactive Elements Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
            <div className="flex items-start space-x-3">
              <Brain className="w-6 h-6 text-blue-600 mt-1" />
              <div>
                <h3 className="font-medium text-blue-900 mb-2">Interactive Learning Features</h3>
                <p className="text-blue-800 text-sm mb-3">
                  This lesson will automatically include interactive elements for learners:
                </p>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li className="flex items-center space-x-2">
                    <Star className="w-4 h-4" />
                    <span>Understanding ratings for each content block</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <MessageSquare className="w-4 h-4" />
                    <span>Note-taking and question submission</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <Target className="w-4 h-4" />
                    <span>Interactive assessments with drag-drop and matching</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4" />
                    <span>Progress tracking and completion certificates</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Save Actions */}
          <div className="flex items-center justify-between pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => onNavigate('creator-detail', { courseId })}
              className="text-gray-600 hover:text-gray-800 transition-colors"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={saving || contentBlocks.length === 0}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
            >
              {saving ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>{isEditing ? 'Updating...' : 'Creating...'}</span>
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5" />
                  <span>{isEditing ? 'Update Lesson' : 'Create Lesson'}</span>
                </>
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

// Sortable Content Block Component
const SortableContentBlock = ({ block, index, onUpdate, onRemove, onDuplicate, onFileUpload }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: block.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <ContentBlockEditor
        block={block}
        index={index}
        onUpdate={onUpdate}
        onRemove={onRemove}
        onDuplicate={onDuplicate}
        onFileUpload={onFileUpload}
        dragHandleProps={listeners}
      />
    </div>
  );
};

// Content Block Editor Component
const ContentBlockEditor = ({ block, index, onUpdate, onRemove, onDuplicate, onFileUpload, dragHandleProps }) => {
  const [expanded, setExpanded] = useState(true);

  const getBlockIcon = (type) => {
    switch (type) {
      case 'video': return Play;
      case 'text': return FileText;
      case 'image': return ImageIcon;
      case 'quiz': return HelpCircle;
      case 'assessment': return Target;
      default: return FileText;
    }
  };

  const Icon = getBlockIcon(block.type);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
      {/* Block Header */}
      <div className="flex items-center justify-between p-4 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center space-x-3">
          <div {...dragHandleProps} className="cursor-move">
            <GripVertical className="w-4 h-4 text-gray-400" />
          </div>
          <Icon className="w-5 h-5 text-gray-600" />
          <div>
            <h4 className="font-medium text-gray-900">
              Block {index + 1}: {block.title || `${block.type} content`}
            </h4>
            <span className="text-sm text-gray-500 capitalize">{block.type}</span>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <button
            type="button"
            onClick={() => onDuplicate(block.id)}
            className="text-gray-400 hover:text-gray-600 p-1"
            title="Duplicate block"
          >
            <Edit3 className="w-4 h-4" />
          </button>
          
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className="text-gray-400 hover:text-gray-600"
          >
            {expanded ? <Eye className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          </button>
          
          <button
            type="button"
            onClick={() => onRemove(block.id)}
            className="text-red-400 hover:text-red-600"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Block Content Editor */}
      {expanded && (
        <div className="p-4 space-y-4">
          {/* Block Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Block Title
            </label>
            <input
              type="text"
              value={block.title}
              onChange={(e) => onUpdate(block.id, { title: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter block title"
            />
          </div>

          {/* Type-specific editors */}
          {block.type === 'video' && (
            <VideoBlockEditor block={block} onUpdate={onUpdate} onFileUpload={onFileUpload} />
          )}

          {block.type === 'text' && (
            <TextBlockEditor block={block} onUpdate={onUpdate} />
          )}

          {block.type === 'image' && (
            <ImageBlockEditor block={block} onUpdate={onUpdate} onFileUpload={onFileUpload} />
          )}

          {block.type === 'quiz' && (
            <QuizBlockEditor block={block} onUpdate={onUpdate} />
          )}

          {block.type === 'assessment' && (
            <AssessmentBlockEditor block={block} onUpdate={onUpdate} />
          )}
        </div>
      )}
    </div>
  );
};

// Video Block Editor
const VideoBlockEditor = ({ block, onUpdate, onFileUpload }) => {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Video URL (YouTube, Vimeo)
        </label>
        <input
          type="url"
          value={block.videoUrl || ''}
          onChange={(e) => onUpdate(block.id, { videoUrl: e.target.value })}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="https://youtube.com/watch?v=..."
        />
      </div>

      <div className="relative">
        <div className="text-center py-4">
          <p className="text-sm text-gray-500 mb-2">Or upload video file</p>
          <input
            type="file"
            accept="video/*"
            onChange={(e) => {
              if (e.target.files[0]) {
                onFileUpload(e.target.files[0], 'video', block.id);
              }
            }}
            className="hidden"
            id={`video-upload-${block.id}`}
          />
          <label
            htmlFor={`video-upload-${block.id}`}
            className="inline-flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg cursor-pointer"
          >
            <Upload className="w-4 h-4 mr-2" />
            Choose File
          </label>
          {block.videoFileUrl && (
            <p className="text-sm text-green-600 mt-2">✓ Video uploaded</p>
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Instructions (optional)
        </label>
        <textarea
          value={block.instructions || ''}
          onChange={(e) => onUpdate(block.id, { instructions: e.target.value })}
          rows={2}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Any instructions for the learner..."
        />
      </div>
    </div>
  );
};

// Text Block Editor
const TextBlockEditor = ({ block, onUpdate }) => {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Content (Markdown supported)
      </label>
      <textarea
        value={block.content || ''}
        onChange={(e) => onUpdate(block.id, { content: e.target.value })}
        rows={8}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
        placeholder="Write your content here. You can use **bold**, *italic*, [links](url), etc."
      />
      <p className="text-xs text-gray-500 mt-1">
        Supports **bold**, *italic*, # headings, - lists, etc.
      </p>
    </div>
  );
};

// Image Block Editor
const ImageBlockEditor = ({ block, onUpdate, onFileUpload }) => {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Image URL
        </label>
        <input
          type="url"
          value={block.imageUrl || ''}
          onChange={(e) => onUpdate(block.id, { imageUrl: e.target.value })}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="https://example.com/image.jpg"
        />
      </div>

      <div className="relative">
        <div className="text-center py-4">
          <p className="text-sm text-gray-500 mb-2">Or upload image file</p>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              if (e.target.files[0]) {
                onFileUpload(e.target.files[0], 'image', block.id);
              }
            }}
            className="hidden"
            id={`image-upload-${block.id}`}
          />
          <label
            htmlFor={`image-upload-${block.id}`}
            className="inline-flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg cursor-pointer"
          >
            <Upload className="w-4 h-4 mr-2" />
            Choose File
          </label>
          {block.imageFileUrl && (
            <p className="text-sm text-green-600 mt-2">✓ Image uploaded</p>
          )}
        </div>
      </div>

      {(block.imageUrl || block.imageFileUrl) && (
        <div className="mt-4">
          <img
            src={block.imageFileUrl || block.imageUrl}
            alt="Preview"
            className="max-w-full h-auto rounded-lg border border-gray-200"
          />
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Caption (optional)
        </label>
        <textarea
          value={block.content || ''}
          onChange={(e) => onUpdate(block.id, { content: e.target.value })}
          rows={2}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Image caption or description..."
        />
      </div>
    </div>
  );
};

// Quiz Block Editor
const QuizBlockEditor = ({ block, onUpdate }) => {
  const addQuestion = () => {
    const newQuestions = [...(block.quizQuestions || []), {
      question: '',
      options: ['', '', '', ''],
      correctAnswer: 0,
      explanation: ''
    }];
    onUpdate(block.id, { quizQuestions: newQuestions });
  };

  const updateQuestion = (questionIndex, field, value) => {
    const newQuestions = [...(block.quizQuestions || [])];
    newQuestions[questionIndex] = { ...newQuestions[questionIndex], [field]: value };
    onUpdate(block.id, { quizQuestions: newQuestions });
  };

  const updateOption = (questionIndex, optionIndex, value) => {
    const newQuestions = [...(block.quizQuestions || [])];
    const newOptions = [...newQuestions[questionIndex].options];
    newOptions[optionIndex] = value;
    newQuestions[questionIndex] = { ...newQuestions[questionIndex], options: newOptions };
    onUpdate(block.id, { quizQuestions: newQuestions });
  };

  const removeQuestion = (questionIndex) => {
    const newQuestions = (block.quizQuestions || []).filter((_, index) => index !== questionIndex);
    onUpdate(block.id, { quizQuestions: newQuestions });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-gray-900">Quiz Questions</h4>
        <button
          type="button"
          onClick={addQuestion}
          className="flex items-center space-x-1 text-blue-600 hover:text-blue-700 text-sm"
        >
          <Plus className="w-4 h-4" />
          <span>Add Question</span>
        </button>
      </div>

      {(block.quizQuestions || []).map((question, questionIndex) => (
        <div key={questionIndex} className="border border-gray-200 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h5 className="font-medium text-gray-800">Question {questionIndex + 1}</h5>
            {(block.quizQuestions?.length || 0) > 1 && (
              <button
                type="button"
                onClick={() => removeQuestion(questionIndex)}
                className="text-red-400 hover:text-red-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          <input
            type="text"
            value={question.question}
            onChange={(e) => updateQuestion(questionIndex, 'question', e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Enter question"
          />

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Answer Options</label>
            {question.options.map((option, optionIndex) => (
              <div key={optionIndex} className="flex items-center space-x-2">
                <input
                  type="radio"
                  name={`correct-${questionIndex}`}
                  checked={question.correctAnswer === optionIndex}
                  onChange={() => updateQuestion(questionIndex, 'correctAnswer', optionIndex)}
                  className="text-blue-600 focus:ring-blue-500"
                />
                <input
                  type="text"
                  value={option}
                  onChange={(e) => updateOption(questionIndex, optionIndex, e.target.value)}
                  className="flex-1 border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={`Option ${optionIndex + 1}`}
                />
              </div>
            ))}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Explanation (optional)
            </label>
            <textarea
              value={question.explanation || ''}
              onChange={(e) => updateQuestion(questionIndex, 'explanation', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={2}
              placeholder="Explain why this is the correct answer"
            />
          </div>
        </div>
      ))}

      {(!block.quizQuestions || block.quizQuestions.length === 0) && (
        <div className="text-center py-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <HelpCircle className="w-8 h-8 text-gray-400 mx-auto mb-2" />
          <p className="text-gray-500 text-sm">No questions yet. Add your first question above.</p>
        </div>
      )}
    </div>
  );
};

// Assessment Block Editor
const AssessmentBlockEditor = ({ block, onUpdate }) => {
  const addQuestion = (type = 'multiple-choice') => {
    const baseQuestion = {
      type,
      question: '',
      explanation: ''
    };

    let newQuestion;
    switch (type) {
      case 'multiple-choice':
        newQuestion = {
          ...baseQuestion,
          options: ['', '', '', ''],
          correctAnswer: 0
        };
        break;
      case 'drag-drop':
        newQuestion = {
          ...baseQuestion,
          instruction: 'Arrange these items in the correct order:',
          items: [
            { id: 'item1', text: 'First item' },
            { id: 'item2', text: 'Second item' },
            { id: 'item3', text: 'Third item' }
          ],
          correctOrder: ['item1', 'item2', 'item3']
        };
        break;
      case 'matching':
        newQuestion = {
          ...baseQuestion,
          instruction: 'Match the items:',
          leftItems: ['Item A', 'Item B', 'Item C'],
          rightItems: ['Match 1', 'Match 2', 'Match 3'],
          correctMatches: { 0: 0, 1: 1, 2: 2 }
        };
        break;
    }

    const newQuestions = [...(block.questions || []), newQuestion];
    onUpdate(block.id, { questions: newQuestions });
  };

  const updateQuestion = (questionIndex, field, value) => {
    const newQuestions = [...(block.questions || [])];
    newQuestions[questionIndex] = { ...newQuestions[questionIndex], [field]: value };
    onUpdate(block.id, { questions: newQuestions });
  };

  const removeQuestion = (questionIndex) => {
    const newQuestions = (block.questions || []).filter((_, index) => index !== questionIndex);
    onUpdate(block.id, { questions: newQuestions });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-gray-900">Interactive Assessment</h4>
        <select
          onChange={(e) => {
            if (e.target.value) {
              addQuestion(e.target.value);
              e.target.value = '';
            }
          }}
          defaultValue=""
          className="text-sm border border-gray-300 rounded px-2 py-1"
        >
          <option value="">Add Question Type...</option>
          <option value="multiple-choice">Multiple Choice</option>
          <option value="drag-drop">Drag & Drop Order</option>
          <option value="matching">Matching</option>
        </select>
      </div>

      {(block.questions || []).map((question, questionIndex) => (
        <div key={questionIndex} className="border border-gray-200 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <h5 className="font-medium text-gray-800">Question {questionIndex + 1}</h5>
              <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded capitalize">
                {question.type.replace('-', ' ')}
              </span>
            </div>
            {(block.questions?.length || 0) > 1 && (
              <button
                type="button"
                onClick={() => removeQuestion(questionIndex)}
                className="text-red-400 hover:text-red-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Question</label>
            <input
              type="text"
              value={question.question}
              onChange={(e) => updateQuestion(questionIndex, 'question', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter your question"
            />
          </div>

          {question.type === 'multiple-choice' && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Answer Options</label>
              {question.options?.map((option, optionIndex) => (
                <div key={optionIndex} className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name={`correct-assessment-${questionIndex}`}
                    checked={question.correctAnswer === optionIndex}
                    onChange={() => updateQuestion(questionIndex, 'correctAnswer', optionIndex)}
                    className="text-blue-600 focus:ring-blue-500"
                  />
                  <input
                    type="text"
                    value={option}
                    onChange={(e) => {
                      const newOptions = [...(question.options || [])];
                      newOptions[optionIndex] = e.target.value;
                      updateQuestion(questionIndex, 'options', newOptions);
                    }}
                    className="flex-1 border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={`Option ${optionIndex + 1}`}
                  />
                </div>
              ))}
            </div>
          )}

          {question.type === 'drag-drop' && (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Instruction</label>
                <input
                  type="text"
                  value={question.instruction || ''}
                  onChange={(e) => updateQuestion(questionIndex, 'instruction', e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  placeholder="Arrange these items in the correct order:"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Items to Arrange</label>
                {question.items?.map((item, itemIndex) => (
                  <div key={itemIndex} className="flex items-center space-x-2 mb-2">
                    <span className="text-sm font-medium text-gray-500 w-8">{itemIndex + 1}.</span>
                    <input
                      type="text"
                      value={item.text}
                      onChange={(e) => {
                        const newItems = [...(question.items || [])];
                        newItems[itemIndex] = { ...item, text: e.target.value };
                        updateQuestion(questionIndex, 'items', newItems);
                      }}
                      className="flex-1 border border-gray-300 rounded px-3 py-2"
                      placeholder={`Item ${itemIndex + 1}`}
                    />
                  </div>
                ))}
                <p className="text-xs text-gray-500 mt-2">
                  Items will be shuffled for learners. The order above is the correct sequence.
                </p>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Explanation (optional)
            </label>
            <textarea
              value={question.explanation || ''}
              onChange={(e) => updateQuestion(questionIndex, 'explanation', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={2}
              placeholder="Explain the correct answer"
            />
          </div>
        </div>
      ))}

      {(!block.questions || block.questions.length === 0) && (
        <div className="text-center py-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <Target className="w-8 h-8 text-gray-400 mx-auto mb-2" />
          <p className="text-gray-500 text-sm">No assessment questions yet. Add interactive questions above.</p>
        </div>
      )}
    </div>
  );
};

// Simple Lesson Preview Component
const LessonPreview = ({ lesson, onClose }) => {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">{lesson.title || 'Untitled Lesson'}</h2>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      {lesson.description && (
        <p className="text-gray-600 mb-6">{lesson.description}</p>
      )}

      <div className="space-y-4">
        {lesson.contentBlocks?.map((block, index) => (
          <div key={block.id} className="border border-gray-200 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-2">
              {index + 1}. {block.title}
            </h3>
            <span className="text-sm text-gray-500 capitalize">{block.type}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CreateLesson;